import Sidebar from '@/components/layout/sidebar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { isUnauthorizedError } from '@/lib/authUtils';
import {
  filterTasksByDateRange,
  generateApprovedTasksPDF,
} from '@/lib/pdfUtils';
import { apiRequest } from '@/lib/queryClient';
import type { TaskWithRelations } from '@shared/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  Calendar,
  CheckCircle,
  Download,
  Filter,
  FolderOpen,
  User,
} from 'lucide-react';
import { useEffect, useState } from 'react';

export default function CompletedTasks() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      toast({
        title: 'Unauthorized',
        description: 'You are logged out. Logging in again...',
        variant: 'destructive',
      });
      setTimeout(() => {
        window.location.href = '/api/login';
      }, 500);
    }
  }, [user, authLoading, toast]);

  // Fetch completed tasks
  const { data: completedTasks = [], isLoading: completedLoading } = useQuery<
    TaskWithRelations[]
  >({
    queryKey: ['/api/tasks', { filter: 'completed' }],
    retry: false,
    queryFn: async () => {
      console.log('[DEBUG] Fetching completed tasks from frontend');
      const response = await fetch('/api/tasks?filter=completed');
      if (!response.ok) {
        console.error(
          '[DEBUG] API call failed:',
          response.status,
          response.statusText
        );
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('[DEBUG] Received completed tasks:', data.length);
      console.log('[DEBUG] Completed tasks data:', data);
      return data;
    },
  });

  // Fetch pending approval tasks (for managers/admins)
  const {
    data: pendingApprovalTasks = [],
    isLoading: pendingLoading,
    error: pendingError,
  } = useQuery<TaskWithRelations[]>({
    queryKey: ['/api/tasks', { filter: 'pending-approval' }],
    retry: false,
    enabled: user?.role === 'manager' || user?.role === 'admin',
    queryFn: async () => {
      console.log('[DEBUG] Fetching pending approval tasks from frontend');
      console.log('[DEBUG] User role:', user?.role);
      const response = await fetch('/api/tasks?filter=pending-approval');
      if (!response.ok) {
        console.error(
          '[DEBUG] API call failed:',
          response.status,
          response.statusText
        );
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('[DEBUG] Received pending approval tasks:', data.length);
      console.log('[DEBUG] Pending approval tasks data:', data);
      return data;
    },
  });

  // Fetch dropped tasks
  const {
    data: droppedTasks = [],
    isLoading: droppedLoading,
    error: droppedError,
  } = useQuery<TaskWithRelations[]>({
    queryKey: ['/api/tasks', { filter: 'dropped' }],
    retry: false,
    queryFn: async () => {
      console.log('[DEBUG] Fetching dropped tasks from frontend');
      const response = await fetch('/api/tasks?filter=dropped');
      if (!response.ok) {
        console.error(
          '[DEBUG] API call failed:',
          response.status,
          response.statusText
        );
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('[DEBUG] Received dropped tasks:', data.length);
      console.log('[DEBUG] Dropped tasks data:', data);
      return data;
    },
  });

  // Log any errors
  if (pendingError) {
    console.error('[DEBUG] Pending approval query error:', pendingError);
  }

  if (droppedError) {
    console.error('[DEBUG] Dropped tasks query error:', droppedError);
  }

  // Approve task mutation
  const approveTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      console.log('[DEBUG] Approving task:', taskId);
      const result = await apiRequest('PATCH', `/api/tasks/${taskId}/approve`);
      console.log('[DEBUG] Approval result:', result);
      return result;
    },
    onSuccess: () => {
      // Invalidate all task-related queries comprehensively
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/analytics/task-stats'],
      });

      // Specifically invalidate completed, pending approval, and dropped tasks
      queryClient.invalidateQueries({
        queryKey: ['/api/tasks', { filter: 'completed' }],
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/tasks', { filter: 'pending-approval' }],
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/tasks', { filter: 'dropped' }],
      });

      // Invalidate all queries that start with '/api/tasks' to catch any variations
      queryClient.invalidateQueries({
        predicate: query =>
          Array.isArray(query.queryKey) && query.queryKey[0] === '/api/tasks',
      });

      toast({
        title: 'Success',
        description: 'Task approved successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to approve task',
        variant: 'destructive',
      });
    },
  });

  // Disapprove task mutation
  const disapproveTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      console.log('[DEBUG] Disapproving task:', taskId);
      const result = await apiRequest(
        'PATCH',
        `/api/tasks/${taskId}/disapprove`
      );
      console.log('[DEBUG] Disapproval result:', result);
      return result;
    },
    onSuccess: () => {
      // Invalidate all task-related queries comprehensively
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/analytics/task-stats'],
      });

      // Specifically invalidate completed, pending approval, and dropped tasks
      queryClient.invalidateQueries({
        queryKey: ['/api/tasks', { filter: 'completed' }],
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/tasks', { filter: 'pending-approval' }],
      });
      queryClient.invalidateQueries({
        queryKey: ['/api/tasks', { filter: 'dropped' }],
      });

      // Invalidate all queries that start with '/api/tasks' to catch any variations
      queryClient.invalidateQueries({
        predicate: query =>
          Array.isArray(query.queryKey) && query.queryKey[0] === '/api/tasks',
      });

      toast({
        title: 'Success',
        description: 'Task disapproved successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to disapprove task',
        variant: 'destructive',
      });
    },
  });

  if (authLoading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading completed tasks...</p>
          </div>
        </div>
      </div>
    );
  }

  const canApprove = user?.role === 'manager' || user?.role === 'admin';

  // Filter approved tasks by date range
  const filteredApprovedTasks = filterTasksByDateRange(
    completedTasks,
    startDate,
    endDate
  );

  // PDF generation function
  const handleDownloadPDF = async () => {
    if (filteredApprovedTasks.length === 0) {
      toast({
        title: 'No Data',
        description: 'No approved tasks found for the selected date range.',
        variant: 'destructive',
      });
      return;
    }

    setIsGeneratingPDF(true);
    try {
      const pdfOptions: any = {
        title: 'Approved Tasks Report',
      };

      if (startDate && endDate) {
        pdfOptions.dateRange = {
          startDate,
          endDate,
        };
      }

      await generateApprovedTasksPDF(filteredApprovedTasks, pdfOptions);

      toast({
        title: 'Success',
        description: 'PDF downloaded successfully',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Debug logging
  console.log('[DEBUG] User role:', user?.role);
  console.log('[DEBUG] Can approve:', canApprove);
  console.log('[DEBUG] Dropped tasks count:', droppedTasks.length);
  console.log(
    '[DEBUG] Filtered approved tasks count:',
    filteredApprovedTasks.length
  );

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
  };

  const TaskCard = ({
    task,
    showApproveButton = false,
  }: {
    task: TaskWithRelations;
    showApproveButton?: boolean;
  }) => {
    // Check if task was completed late
    const isCompletedLate =
      task.status === 'complete' &&
      task.completedAt &&
      task.dueDate &&
      new Date(task.completedAt) > new Date(task.dueDate);

    return (
      <Card
        key={task.id}
        className={`mb-4 relative ${
          task.status === 'dropped'
            ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600'
            : isCompletedLate
              ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600'
              : ''
        }`}
      >
        {/* Green checkmark overlay for approved tasks */}
        {task.approvedAt && task.status !== 'dropped' && (
          <div className="absolute top-4 right-4 z-10">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
          </div>
        )}
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold text-gray-900 mb-2">
                {task.title}
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                {task.project && task.project.color && (
                  <Badge
                    variant="outline"
                    className="text-xs"
                    style={{
                      backgroundColor: `${task.project.color}20`,
                      color: task.project.color,
                    }}
                  >
                    <FolderOpen className="w-3 h-3 mr-1" />
                    {task.project.name}
                  </Badge>
                )}
                {task.status === 'dropped' ? (
                  <Badge variant="destructive" className="text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Dropped
                  </Badge>
                ) : (
                  <>
                    <Badge variant="secondary" className="text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Complete
                    </Badge>
                    {isCompletedLate && (
                      <Badge variant="destructive" className="text-xs">
                        Late
                      </Badge>
                    )}
                    {task.approvedAt && (
                      <Badge variant="default" className="text-xs bg-green-600">
                        <Award className="w-3 h-3 mr-1" />
                        Approved
                      </Badge>
                    )}
                  </>
                )}
              </div>
            </div>
            {showApproveButton && !task.approvedAt && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => approveTaskMutation.mutate(task.id)}
                  disabled={
                    approveTaskMutation.isPending ||
                    disapproveTaskMutation.isPending
                  }
                  className="bg-green-600 hover:bg-green-700"
                  data-testid={`button-approve-${task.id}`}
                >
                  {approveTaskMutation.isPending ? 'Approving...' : 'Approve'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => disapproveTaskMutation.mutate(task.id)}
                  disabled={
                    approveTaskMutation.isPending ||
                    disapproveTaskMutation.isPending
                  }
                  className="bg-red-600 hover:bg-red-700"
                  data-testid={`button-disapprove-${task.id}`}
                >
                  {disapproveTaskMutation.isPending
                    ? 'Disapproving...'
                    : 'Disapprove'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {task.description && (
            <p className="text-gray-600 mb-4">{task.description}</p>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="flex items-center text-gray-500 mb-1">
                <User className="w-4 h-4 mr-1" />
                Assignee
              </div>
              <div className="font-medium">
                {task.assignee
                  ? `${task.assignee.firstName} ${task.assignee.lastName}`
                  : 'Unassigned'}
              </div>
            </div>

            <div>
              <div className="flex items-center text-gray-500 mb-1">
                <Calendar className="w-4 h-4 mr-1" />
                Due Date
              </div>
              <div className="font-medium">{formatDate(task.dueDate)}</div>
            </div>

            <div>
              <div className="flex items-center text-gray-500 mb-1">
                <CheckCircle className="w-4 h-4 mr-1" />
                {task.status === 'dropped' ? 'Dropped' : 'Completed'}
              </div>
              <div className="font-medium">
                {task.status === 'dropped'
                  ? formatDate(task.updatedAt)
                  : formatDate(task.completedAt)}
              </div>
            </div>

            <div>
              <div className="flex items-center text-gray-500 mb-1">
                <Award className="w-4 h-4 mr-1" />
                Status
              </div>
              <div className="font-medium">
                {task.status === 'dropped' ? (
                  <span className="text-red-600">Dropped</span>
                ) : task.approvedAt ? (
                  <span className="text-green-600">Approved</span>
                ) : (
                  <span className="text-orange-600">Pending Review</span>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center text-gray-500 mb-1">
                <Calendar className="w-4 h-4 mr-1" />
                Last Updated
              </div>
              <div className="font-medium">
                {task.updatedAt
                  ? new Date(task.updatedAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: 'UTC',
                    })
                  : 'Unknown'}
              </div>
            </div>
          </div>

          {task.status === 'dropped' ? (
            <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="text-sm">
                <span className="text-gray-600">Status: </span>
                <span className="text-red-600 font-medium">
                  Task was dropped/cancelled
                </span>
              </div>
            </div>
          ) : (
            task.completedAt &&
            task.dueDate && (
              <div
                className={`mt-4 p-3 rounded-lg ${
                  isCompletedLate
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    : 'bg-gray-50 dark:bg-gray-800'
                }`}
              >
                <div className="text-sm">
                  <span className="text-gray-600">Delivery: </span>
                  {new Date(task.completedAt) <= new Date(task.dueDate) ? (
                    <span className="text-green-600 font-medium">On Time</span>
                  ) : (
                    <span className="text-red-600 font-medium">
                      Late by{' '}
                      {Math.ceil(
                        (new Date(task.completedAt).getTime() -
                          new Date(task.dueDate).getTime()) /
                          (1000 * 60 * 60 * 24)
                      )}{' '}
                      days
                    </span>
                  )}
                </div>
              </div>
            )
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="mb-6">
            <h1
              className="text-3xl font-bold text-gray-900"
              data-testid="text-page-title"
            >
              Completed Tasks
            </h1>
            <p className="text-gray-600 mt-2">
              View and manage completed tasks and approvals
            </p>
          </div>

          <Tabs
            defaultValue={canApprove ? 'pending' : 'approved'}
            className="w-full"
          >
            <TabsList
              className={`grid w-full ${canApprove ? 'grid-cols-3' : 'grid-cols-2'}`}
            >
              {canApprove && (
                <TabsTrigger value="pending" data-testid="tab-pending-approval">
                  Pending Approval ({pendingApprovalTasks.length})
                </TabsTrigger>
              )}
              <TabsTrigger value="approved" data-testid="tab-approved">
                Approved Tasks ({completedTasks.length})
              </TabsTrigger>
              <TabsTrigger value="dropped" data-testid="tab-dropped">
                Dropped Tasks ({droppedTasks.length})
              </TabsTrigger>
            </TabsList>

            {canApprove && (
              <TabsContent value="pending" className="mt-6">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Tasks Pending Approval
                  </h2>
                  <p className="text-gray-600">
                    Review and approve completed tasks from your team members
                  </p>
                </div>

                {pendingLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading pending tasks...</p>
                  </div>
                ) : pendingApprovalTasks.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-8">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No tasks pending approval
                      </h3>
                      <p className="text-gray-500">
                        All completed tasks have been reviewed and approved.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div>
                    {pendingApprovalTasks.map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        showApproveButton={true}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>
            )}

            <TabsContent value="approved" className="mt-6">
              <div className="mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      Approved Tasks
                    </h2>
                    <p className="text-gray-600">
                      Archive of all approved and completed tasks
                    </p>
                  </div>
                  <Button
                    onClick={handleDownloadPDF}
                    disabled={
                      isGeneratingPDF || filteredApprovedTasks.length === 0
                    }
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {isGeneratingPDF ? 'Generating PDF...' : 'Download PDF'}
                  </Button>
                </div>

                {/* Date Filter Section */}
                <Card className="mb-6">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center">
                      <Filter className="w-4 h-4 mr-2" />
                      Filter by Date Range
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label
                          htmlFor="start-date"
                          className="text-sm font-medium"
                        >
                          Start Date
                        </Label>
                        <Input
                          id="start-date"
                          type="date"
                          value={startDate}
                          onChange={e => setStartDate(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="end-date"
                          className="text-sm font-medium"
                        >
                          End Date
                        </Label>
                        <Input
                          id="end-date"
                          type="date"
                          value={endDate}
                          onChange={e => setEndDate(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Showing {filteredApprovedTasks.length} of{' '}
                        {completedTasks.length} approved tasks
                        {startDate &&
                          endDate &&
                          ` (${startDate} to ${endDate})`}
                      </p>
                      {(startDate || endDate) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setStartDate('');
                            setEndDate('');
                          }}
                        >
                          Clear Filter
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {completedLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading completed tasks...</p>
                </div>
              ) : filteredApprovedTasks.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <Award className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {startDate || endDate
                        ? 'No approved tasks found'
                        : 'No approved tasks yet'}
                    </h3>
                    <p className="text-gray-500">
                      {startDate || endDate
                        ? 'No approved tasks found for the selected date range. Try adjusting your filters.'
                        : 'Completed tasks will appear here after manager approval.'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div>
                  {filteredApprovedTasks.map(task => (
                    <TaskCard key={task.id} task={task} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="dropped" className="mt-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Dropped Tasks
                </h2>
                <p className="text-gray-600">
                  Archive of all dropped/cancelled tasks
                </p>
              </div>

              {droppedLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading dropped tasks...</p>
                </div>
              ) : droppedTasks.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No dropped tasks
                    </h3>
                    <p className="text-gray-500">
                      No tasks have been dropped or cancelled yet.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div>
                  {droppedTasks.map(task => (
                    <TaskCard key={task.id} task={task} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
